/**
 * In-page notification component (toast-style).
 *
 * Auto-dismisses after a timeout.
 * Shows spinner for loading/info notifications.
 */

import { useEffect, type ReactNode } from 'react';
import { NOTIFICATION_SIZE as SIZE, notificationAnimation } from '@/utils/notificationLayout';

export interface NotificationProps {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  showSpinner?: boolean;
  icon?: ReactNode;
  /** A button in the toast (e.g. Undo). Clicking it runs `onClick` and dismisses the toast. */
  action?: { label: string; onClick: () => void };
  onDismiss: (id: string) => void;
}

export function Notification({
  id,
  message,
  type,
  duration = 3000,
  showSpinner = false,
  icon,
  action,
  onDismiss,
}: NotificationProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return '#d4edda';
      case 'error':
        return '#f8d7da';
      case 'warning':
        return '#fff3cd';
      case 'info':
      default:
        return '#d1ecf1';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return '#155724';
      case 'error':
        return '#721c24';
      case 'warning':
        return '#856404';
      case 'info':
      default:
        return '#0c5460';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const accent = getTextColor();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SIZE.innerGap,
        padding: `${SIZE.padY}px ${SIZE.padX}px`,
        marginBottom: SIZE.gap,
        background: getBackgroundColor(),
        color: accent,
        borderRadius: SIZE.radius,
        // A stripe in the type's colour and a firmer shadow, so a toast reads against a busy page.
        borderLeft: `${SIZE.accent}px solid ${accent}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
        fontSize: SIZE.font,
        minWidth: SIZE.minWidth,
        maxWidth: SIZE.maxWidth,
        animation: notificationAnimation(),
        pointerEvents: 'auto',
      }}
    >
      {/* Spinner for info notifications */}
      {showSpinner && type === 'info' && (
        <div
          style={{
            width: SIZE.spinner,
            height: SIZE.spinner,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      )}

      {/* Icon for non-spinner notifications */}
      {!showSpinner && (
        <div
          style={{
            fontSize: SIZE.icon,
            fontWeight: 'bold',
          }}
        >
          {icon ?? getIcon()}
        </div>
      )}

      {/* Message */}
      <div style={{ flex: 1 }}>{message}</div>

      {action && (
        <button
          onClick={() => { action.onClick(); onDismiss(id); }}
          style={{
            background: 'white', color: accent, border: `1px solid ${accent}`, borderRadius: 6,
            padding: '4px 12px', fontSize: 'inherit', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          }}
        >
          {action.label}
        </button>
      )}

      {/* Close button (only if not auto-dismissing immediately) */}
      {duration > 1000 && (
        <button
          onClick={() => onDismiss(id)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'currentColor',
            cursor: 'pointer',
            fontSize: SIZE.close,
            padding: '0',
            opacity: 0.7,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
